// shop-frontend/src/components/PushNotificationPrompt.jsx
import React, { useState, useEffect } from "react";
import {
  isPushSupported,
  getPermissionState,
  subscribeToPush,
  isSubscribed,
} from "../utils/pushNotifications";

// How long "Not now" suppresses the prompt before it may gently re-offer.
const SNOOZE_DAYS = 15;
const SNOOZE_KEY = "shopPushPromptSnoozedUntil";

function isSnoozed() {
  try {
    const until = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    return until && Date.now() < until;
  } catch {
    return false;
  }
}

function snooze(days = SNOOZE_DAYS) {
  try {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
  } catch { /* ignore */ }
}

// iOS only allows web push for sites INSTALLED to the Home Screen (PWA,
// iOS 16.4+). In a normal Safari tab push is unavailable, so we detect iOS and
// guide the user to "Add to Home Screen" instead of silently showing nothing.
function isIOS() {
  const ua = navigator.userAgent || "";
  const iOSDevice = /iPhone|iPad|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

function isStandalonePWA() {
  return (
    window.navigator.standalone === true ||
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches)
  );
}

export default function PushNotificationPrompt() {
  const [visible, setVisible] = useState(false);
  // "subscribe" = can enable push now; "ios-install" = iOS tab, must install first
  const [mode, setMode] = useState("subscribe");

  useEffect(() => {
    (async () => {
      // Respect a previous "Not now" across logins on every device (localStorage,
      // not sessionStorage, so the choice survives logout/new session).
      if (isSnoozed()) return;
      if (getPermissionState() === "denied") return;

      if (isPushSupported()) {
        if (await isSubscribed()) return;
        setMode("subscribe");
      } else if (isIOS() && !isStandalonePWA()) {
        setMode("ios-install");
      } else {
        return;
      }

      const timer = setTimeout(() => setVisible(true), 5000);
      return () => clearTimeout(timer);
    })();
  }, []);

  if (!visible) return null;

  const handleEnable = async () => {
    const result = await subscribeToPush();
    if (result && result.ok) {
      setVisible(false);
    } else {
      // Permission denied or error — snooze so we don't re-prompt every login.
      snooze();
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    snooze();
    setVisible(false);
  };

  if (mode === "ios-install") {
    return (
      <div style={styles.banner}>
        <div style={styles.content}>
          <span style={styles.icon}>🔔</span>
          <span style={styles.text}>
            To get new-order &amp; message alerts on iPhone, tap{" "}
            <span style={{ fontWeight: 700 }}>Share</span> →{" "}
            <span style={{ fontWeight: 700 }}>Add to Home Screen</span>, then open Moondala from there.
          </span>
        </div>
        <div style={styles.buttons}>
          <button onClick={handleDismiss} style={styles.dismissBtn}>Got it</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.banner}>
      <div style={styles.content}>
        <span style={styles.icon}>🔔</span>
        <span style={styles.text}>
          Get notified about new orders, messages & returns
        </span>
      </div>
      <div style={styles.buttons}>
        <button onClick={handleEnable} style={styles.enableBtn}>Enable</button>
        <button onClick={handleDismiss} style={styles.dismissBtn}>Not now</button>
      </div>
    </div>
  );
}

const styles = {
  banner: {
    position: "fixed",
    bottom: 20,
    left: "50%",
    transform: "translateX(-50%)",
    background: "linear-gradient(135deg, #1a1a2e, #16213e)",
    color: "#fff",
    borderRadius: 14,
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    zIndex: 9999,
    maxWidth: "90vw",
    width: 420,
    fontFamily: "inherit",
  },
  content: { display: "flex", alignItems: "center", gap: 10, flex: 1 },
  icon: { fontSize: 22 },
  text: { fontSize: 14, lineHeight: 1.4 },
  buttons: { display: "flex", gap: 8, flexShrink: 0 },
  enableBtn: {
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  dismissBtn: {
    background: "transparent",
    color: "#aaa",
    border: "1px solid #444",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    cursor: "pointer",
  },
};
